import { NavigationItem } from '@/types/navigation';
import api from './api';

export class NavigationService {
  private static instance: NavigationService;
  private baseUrl = '/navigation';

  public static getInstance(): NavigationService {
    if (!NavigationService.instance) {
      NavigationService.instance = new NavigationService();
    }
    return NavigationService.instance;
  }

  private getAuthHeaders() {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      throw new Error('No authentication token available');
    }
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }

  /**
   * Search navigation items
   */
  async searchNavigationItems(query: string, limit: number = 10): Promise<{
    items: NavigationItem[];
    total: number;
    search_time: number;
    suggestions: string[];
  }> {
    const response = await api.get(`${this.baseUrl}/search?q=${encodeURIComponent(query)}&limit=${limit}`);
    return response.data.data;
  }

  /**
   * Get navigation suggestions for user
   */
  async getNavigationSuggestions(limit: number = 10): Promise<{
    suggestions: NavigationItem[];
    total: number;
  }> {
    const response = await fetch(
      `${this.baseUrl}/suggestions?limit=${limit}`,
      {
        headers: this.getAuthHeaders()
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to get suggestions: ${response.statusText}`);
    }

    const data = await response.json();
    return data.data;
  }

  /**
   * Get recent navigation items
   */
  async getRecentNavigation(limit: number = 10): Promise<{
    recent_items: NavigationItem[];
    total: number;
  }> {
    const response = await api.get(`${this.baseUrl}/recent?limit=${limit}`);
    return response.data.data;
  }

  /**
   * Get user's navigation favorites
   */
  async getFavorites(): Promise<{
    favorites: NavigationItem[];
    total: number;
  }> {
    const response = await api.get(`${this.baseUrl}/favorites`);
    return response.data.data;
  }

  /**
   * Add navigation item to favorites
   */
  async addFavorite(navigationItem: {
    navigation_module_id?: string;
    navigation_screen_id?: string;
    navigation_node_id?: string;
  }): Promise<{
    favorite_id: string;
    created_at: string;
  }> {
    const response = await fetch(`${this.baseUrl}/favorites`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(navigationItem)
    });

    if (!response.ok) {
      throw new Error(`Failed to add favorite: ${response.statusText}`);
    }

    const data = await response.json();
    return data.data;
  }

  /**
   * Remove navigation item from favorites
   */
  async removeFavorite(itemId: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/favorites/${itemId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`Failed to remove favorite: ${response.statusText}`);
    }
  }

  /**
   * Check if navigation item is in favorites
   */
  async checkFavorite(navigationItem: {
    navigation_module_id?: string;
    navigation_screen_id?: string;
    navigation_node_id?: string;
  }): Promise<{
    is_favorite: boolean;
    favorite_id: string | null;
  }> {
    const params = new URLSearchParams();
    if (navigationItem.navigation_module_id) {
      params.append('navigation_module_id', navigationItem.navigation_module_id);
    }
    if (navigationItem.navigation_screen_id) {
      params.append('navigation_screen_id', navigationItem.navigation_screen_id);
    }
    if (navigationItem.navigation_node_id) {
      params.append('navigation_node_id', navigationItem.navigation_node_id);
    }

    const response = await fetch(
      `${this.baseUrl}/favorites/check?${params.toString()}`,
      {
        headers: this.getAuthHeaders()
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to check favorite: ${response.statusText}`);
    }

    const data = await response.json();
    return data.data;
  }

  /**
   * Track navigation usage
   */
  async trackNavigationUsage(trackingData: {
    navigation_module_id?: string;
    navigation_screen_id?: string;
    navigation_node_id?: string;
    action_type: string;
    search_term?: string;
    session_id?: string;
    metadata?: any;
  }): Promise<void> {
    const response = await fetch(`${this.baseUrl}/track`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(trackingData)
    });

    if (!response.ok) {
      console.error(`Failed to track navigation: ${response.statusText}`);
    }
  }
}

export const navigationService = NavigationService.getInstance();
